import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import API from 'api/API'
import { CardLimit, CardLimits, CardServices } from 'api/Models'
import cardArt from 'assets/imgs/icons/virtual-card.svg'
import iconStolen from 'assets/imgs/icons/report-stolen.svg'
import iconClose from 'assets/imgs/icons/close-card.svg'
import iconEdit from 'assets/imgs/icons/edit.svg'
import { toast } from 'react-toastify'
import { defaultToastOptions, formatCurrency, getCardArtClass, splitCardPan, zeroPad } from 'utils'
import { CircleSpinnerOverlay } from 'react-spinner-overlay'
import { Col, Button } from 'react-bootstrap'
import { CardData } from 'api/Models'
import Label from 'components/elements/Label'
import Value from 'components/elements/Value'
import BasePage from 'components/pages/BasePage'
import CardLimitsModal from '../elements/CardLimitsForm'
import { LimitType } from '../../enums'
import PageHeader from '../elements/PageHeader'
import IconButton from '../elements/IconButton'
import creditCardBan from '../../assets/imgs/icons/credit-card-ban.svg'
import TextEditModal from 'components/elements/modals/TextEditModal'

interface CardViewProps {
    card: CardData
}

function CardView(props: CardViewProps) {
    return (
        <>
            <div>
                <div className={`cd cd-page ${getCardArtClass(props.card?.cardDesign ?? 1)} d-flex flex-column m-auto`}>
                    <div className="p-2 mb-auto d-inline-flex card-icon-container">
                        <div className="card-icon">
                            <img src={cardArt} alt="" />
                        </div>
                        {props.card?.type.toUpperCase()}
                    </div>
                    <div className="p-2">
                        <label>Card Number</label>
                        <div className="card-number card-value">
                            {splitCardPan(props.card.processorData?.obscuredPan)}{' '}
                        </div>
                    </div>
                    {/*<div className="card-expire-label">Expiry date</div>*/}
                    <div className="p-2">
                        <label>Expiry date</label>
                        <div className="card-expire card-value">
                            {zeroPad(props.card.processorData?.expirationMonth ?? 0, 2)}/
                            {props.card.processorData?.expirationYear}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

interface HeaderControlsProps {
    cardId: string
    cardStatus: string | null
    setBusy: (Value: boolean) => void
    updateStatus: (status: string | null) => void
}

function HeaderControls(props: HeaderControlsProps) {
    const freezeCard = async () => {
        props.setBusy(true)
        const result = await API.Cards.Freeze(props.cardId)
        if (result.success) {
            props.updateStatus('BlockedByCustomer')
        }
        props.setBusy(false)
    }

    const unfreezeCard = async () => {
        props.setBusy(true)
        const result = await API.Cards.Unfreeze(props.cardId)
        if (result.success) {
            props.updateStatus('Active')
        } else {
            toast.error('Failed to unfreeze card: ' + result.error, defaultToastOptions())
            console.log('Error', result)
        }

        props.setBusy(false)
    }

    return (
        <>
            <Col className="me-auto"></Col>
            <Col>
                <div className="page-nav">
                    {props.cardStatus === 'BlockedByCustomer' || props.cardStatus === 'Closed' ? (
                        <IconButton icon={creditCardBan} label="Unfreeze Card" onClick={unfreezeCard} type="primary" />
                    ) : (
                        <IconButton icon={creditCardBan} label="Freeze Card" onClick={freezeCard} type="dark" />
                    )}
                </div>
            </Col>
        </>
    )
}

interface LimitsProps {
    label: string
    limit: CardLimit
    oldLimit: CardLimit
    limitType: LimitType
    currency: string | null
    onChange: (i: LimitType) => void
}

interface LimitsBlockProps {
    currency: string | null
    id: string | undefined
    cardLimits: CardLimits | null
    changeBusy: (isBusy: boolean) => void
    changeCardLimit: (cl: CardLimits) => void
}

function LimitsBlock(props: LimitsBlockProps) {
    const [isShownModal, setIsShowModal] = useState(false)
    const [initialTab, setInitialTab] = useState(LimitType.Daily)
    const [cardLimits, setCardLimits] = useState(props.cardLimits)

    const onChange = (limitType: LimitType) => {
        setInitialTab(limitType)
        setIsShowModal(true)
    }

    const onSubmit = (cardLimits: CardLimits) => {
        console.log('On submit', cardLimits)
        setCardLimits(cardLimits)
        setIsShowModal(false)
    }

    const submitCardLimits = () => {
        if (cardLimits && props.id) {
            props.changeBusy(true)
            API.Cards.SetCardUsageLimits(props.id, {
                ...cardLimits,
            })
                .then((r) => {
                    console.log('Set card usage', r)

                    if (r.success) {
                        toast.success('Card limits changed', defaultToastOptions())
                        props.changeCardLimit(cardLimits)
                    } else {
                        toast.error(r.error, defaultToastOptions())
                    }
                })
                .finally(() => props.changeBusy(false))
        }
    }

    const compareLimit = (a: CardLimit, b: CardLimit) => {
        return (
            a.cashAmount === b.cashAmount &&
            a.atmUseCounter === b.atmUseCounter &&
            a.nonCacheAmount === b.nonCacheAmount &&
            a.posUseCounter === b.posUseCounter &&
            a.internetAmount === b.internetAmount &&
            a.internetUseCounter === b.internetUseCounter &&
            a.motoAmount === b.motoAmount &&
            a.motoUseCounter === b.motoUseCounter &&
            a.totalAmount === b.totalAmount
        )
    }

    const compareLimits = (a: CardLimits, b: CardLimits) => {
        return compareLimit(a.daily, b.daily) && compareLimit(a.weekly, b.weekly) && compareLimit(a.monthly, b.monthly)
    }

    const isDisabled = props.cardLimits === null
        || cardLimits === null
        || compareLimits(cardLimits, props.cardLimits)

    console.log('Is disabled')

    return (
        <div className="block position-relative">

            {props.cardLimits === null || cardLimits === null ? (
                <>
                    <div className="disabled-overlay" />
                    <DummyLimits
                        label={'Daily'}
                        currency={props.currency}
                    />
                    <DummyLimits
                        label={'Weekly'}
                        currency={props.currency}
                    />
                    <DummyLimits
                        label={'Monthly'}
                        currency={props.currency}
                    />
                </>
            ) : (
                <>
                    <Limits
                        label={'Daily'}
                        limitType={LimitType.Daily}
                        limit={cardLimits.daily}
                        oldLimit={props.cardLimits.daily}
                        currency={props.currency}
                        onChange={onChange}
                    />
                    <Limits
                        label={'Weekly'}
                        limitType={LimitType.Weekly}
                        limit={cardLimits.weekly}
                        oldLimit={props.cardLimits.weekly}
                        currency={props.currency}
                        onChange={onChange}
                    />
                    <Limits
                        label={'Monthly'}
                        limitType={LimitType.Monthly}
                        limit={cardLimits.monthly}
                        oldLimit={props.cardLimits.monthly}
                        currency={props.currency}
                        onChange={onChange}
                    />
                    {isShownModal && (
                        <CardLimitsModal
                            show={isShownModal}
                            onApply={(cl) => onSubmit(cl as CardLimits)}
                            onCloseModal={() => setIsShowModal(false)}
                            originalLimits={cardLimits}
                            initialTab={initialTab}
                        />
                    )}
                </>
            )}

            <Button onClick={submitCardLimits} disabled={isDisabled}>
                Send New Limits
            </Button>
        </div>
    )
}

function DummyLimits(props: { label: string, currency: string | null }) {

    const li = (label: string) => {
        return (
            <li>
                <div className="label">
                    N/A
                    <span>{label}</span>
                </div>
                <div className="func">
                    <div>
                        <img src={iconEdit} alt="" />
                    </div>
                </div>
            </li>
        )
    }

    return (
        <>
            <div className="block">
                <div className="block-heading">Spending limits: {props.label}</div>
                <div className="block-list">
                    <ul>
                        {li('Cash')}
                        {li('POS')}
                        {li('Internet')}
                        {li('MOTO')}
                        {li('Total')}
                    </ul>
                </div>
            </div>
        </>
    )
}

function Limits(props: LimitsProps) {
    const li = (label: string, amount: number, oldAmount: number, useCount?: number, oldUseCount?: number) => {
        const isNew = amount !== oldAmount || useCount !== oldUseCount
        const useCountStr = useCount !== undefined ? '(' + useCount + ')' : ''
        return (
            <li>
                <div className={'label' + (isNew ? ' new' : '')}>
                    {formatCurrency(amount, props.currency)} {useCountStr}
                    <span>{label}</span>
                </div>
                <div className="func">
                    <div onClick={() => props.onChange(props.limitType)}>
                        <img src={iconEdit} alt="" />
                    </div>
                </div>
            </li>
        )
    }

    return (
        <>
            <div className="block">
                <div className="block-heading">Spending limits: {props.label}</div>
                <div className="block-list">
                    <ul>
                        {li(
                            'Cash',
                            props.limit.cashAmount,
                            props.oldLimit.cashAmount,
                            props.limit.atmUseCounter,
                            props.oldLimit.atmUseCounter
                        )}
                        {li(
                            'POS',
                            props.limit.nonCacheAmount,
                            props.oldLimit.nonCacheAmount,
                            props.limit.posUseCounter,
                            props.oldLimit.posUseCounter
                        )}
                        {li(
                            'Internet',
                            props.limit.internetAmount,
                            props.oldLimit.internetAmount,
                            props.limit.internetUseCounter,
                            props.oldLimit.internetUseCounter
                        )}
                        {li(
                            'MOTO',
                            props.limit.motoAmount,
                            props.oldLimit.motoAmount,
                            props.limit.motoUseCounter,
                            props.oldLimit.motoUseCounter
                        )}
                        {li('Total', props.limit.totalAmount, props.oldLimit.totalAmount)}
                    </ul>
                </div>
            </div>
        </>
    )
}

function StatusTag(props: { status: string | null }) {
    switch (props.status) {
        case 'Active':
            return <div className="status active">Active</div>
        case 'Inactive':
            return <div className="status inactive">Inactive</div>
        case 'BlockedByCustomer':
            return <div className="status inactive">Frozen</div>
        case 'BlockedByClient':
            return <div className="status inactive">Frozen</div>
        case 'BlockedByIssuer':
            return <div className="status inactive">Frozen</div>
        case 'BlockedByPartner':
            return <div className="status inactive">Frozen</div>
        case 'BlockedByCardProcessor':
            return <div className="status inactive">Frozen</div>
        case 'Stolen':
            return <div className="status inactive">Reported Stolen</div>
        case 'Lost':
            return <div className="status inactive">Reported Lost</div>
        case 'Closed':
            return <div className="status inactive">Closed</div>
        default:
            return <div className="status pending">{props.status}</div>
    }
}

interface CardControlsProps {
    cardId: string
    cardStatus: string | null
    setBusy: (Value: boolean) => void
    updateStatus: (status: string) => void
}

function CardControls(props: CardControlsProps) {
    const freezeCard = async () => {
        props.setBusy(true)
        const result = await API.Cards.Freeze(props.cardId)
        if (result.success) {
            props.updateStatus('BlockedByCustomer')
        }
        props.setBusy(false)
    }

    const unfreezeCard = async () => {
        props.setBusy(true)
        const result = await API.Cards.Unfreeze(props.cardId)
        if (result.success) {
            props.updateStatus('Active')
        } else {
            toast.error('Failed to unfreeze card: ' + result.error, defaultToastOptions())
            console.log('Error', result)
        }

        props.setBusy(false)
    }

    const reportStolen = async () => {
        const confirm = window.confirm('Report stolen? This will block the card. This is not reversible.')
        if (!confirm) {
            return
        }
        props.setBusy(true)
        const result = await API.Cards.ReportStolen(props.cardId)
        if (result.success) {
            props.updateStatus('Stolen')
        }
        props.setBusy(false)
    }

    const close = async () => {
        const confirm = window.confirm('Close card? This will block the card. This is not reversible.')
        if (!confirm) {
            return
        }
        props.setBusy(true)
        const result = await API.Cards.Close(props.cardId)
        if (result.success) {
            props.updateStatus('Closed')
        }
        props.setBusy(false)
    }

    return (
        <div className="block-nav">
            <div className="row">
                {props.cardStatus === 'Active' && (
                    <div className="col">
                        <button onClick={freezeCard}>
                            <div className="nav-icn">
                                <img src={iconStolen} alt="" />
                            </div>
                            <div className="nav-link">Freeze Cards</div>
                        </button>
                    </div>
                )}
                {(props.cardStatus === 'BlockedByCustomer' || props.cardStatus === 'Closed') && (
                    <div className="col">
                        <button onClick={unfreezeCard}>
                            <div className="nav-icn">
                                <img src={iconStolen} alt="" />
                            </div>
                            <div className="nav-link">Unfreeze Cards</div>
                        </button>
                    </div>
                )}
                <div className="col">
                    <button onClick={reportStolen}>
                        <div className="nav-icn">
                            <img src={iconStolen} alt="" />
                        </div>
                        <div className="nav-link">Report Stolen</div>
                    </button>
                </div>
                <div className="col">
                    <button onClick={close}>
                        <div className="nav-icn red">
                            <img src={iconClose} alt="" />
                        </div>
                        <div className="nav-link red">Close card</div>
                    </button>
                </div>
            </div>
        </div>
    )
}

interface InfoEntryProps {
    label: string
    value: any
    editable?: boolean
    onChange?: (value: any) => void
}

function InfoEntry(props: InfoEntryProps) {
    return (
        <li>
            <div className="label">
                {props.value.toString()}
                <span> {props.label}</span>
            </div>
            {props.editable && (
                <div className="func">
                    <div>
                        <img src={iconEdit} alt="" onClick={() => props.onChange?.(props.value)} />
                    </div>
                </div>
            )}
        </li>
    )
}

interface InfoPanelProps {
    card: CardData
    onAliasChange: (alias: string) => void
}

function InfoPanel(props: InfoPanelProps) {
    const [isShownModal, setIsShownModal] = useState(false)
    const [busy, setBusy] = useState(false)

    const onSubmit = (value: string) => {
        console.log('onSubmit', value)
        setIsShownModal(false)

        if (!props.card.id) {
            toast.error('Card ID is not set', defaultToastOptions())
            setBusy(false)
            return
        }

        if (busy) {
            return
        }

        setBusy(true)
        if (!props.card.id) {
            toast.error('Card ID is not set', defaultToastOptions())
            setBusy(false)
            return
        }

        API.Cards.UpdateCardAlias(props.card.id, value).then((result) => {
            if (result.success) {
                toast.success('Custom name updated', defaultToastOptions())
                props.onAliasChange(value)
            } else {
                toast.error('Failed to update custom name: ' + result.errorMessage, defaultToastOptions())
            }
        }).catch((error) => {
            toast.error('Failed to update custom name', defaultToastOptions())
        }).finally(() => {
            setBusy(false)
        })
    }


    return (
        <>
            <div className="block block-cd-details">
                <div className="block-heading">Card details</div>
                {/* <div className="func">
                    <div>
                        <img src={iconEdit} alt=""/>
                    </div>
                </div> */}
                <div className="block-list">
                    <ul>
                        <InfoEntry label={'Custom Name'} value={props.card.alias ?? props.card.processorData?.embossName} editable={true} onChange={() => setIsShownModal(true)} />
                        <InfoEntry label={'Linked user'} value={props.card.cardholderName} editable={true} onChange={() => alert('Custom name changed')} />
                        <InfoEntry label={'Created on'} value={new Date(props.card.createdOn).toDateString()} />
                        <InfoEntry label={'Modified on'} value={new Date(props.card.modifiedOn ?? 0).toDateString()} />
                        <InfoEntry
                            label={'Activated on'}
                            value={
                                props.card.activatedPlasticAt
                                    ? new Date(props.card.activatedPlasticAt).toDateString()
                                    : 'Not activated'
                            }
                        />
                    </ul>
                </div>
                {isShownModal && (
                    <TextEditModal
                        title='Edit custom name?'
                        show={isShownModal}
                        original={props.card.processorData?.embossName ?? ''}
                        placeholder='Custom name'
                        onApply={(cl) => onSubmit(cl as string)}
                        onCloseModal={() => setIsShownModal(false)}
                    />
                )}
            </div>
        </>
    )
}

function ServiceEntry(props: { label: string; desc?: string; value: boolean; onChange: (value: boolean) => void }) {
    return (
        <li>
            <Label>
                {props.label} {props.desc && <span>{props.desc}</span>}
            </Label>
            <div className="func">
                <label className="slide-button">
                    <input
                        type="checkbox"
                        defaultChecked={props.value}
                        onChange={(c) => props.onChange(c.target.checked)}
                    />
                    <span className="slide-indicator"></span>
                    <span className="slide-label"></span>
                </label>
            </div>
        </li>
    )
}

function ServicesPanel(props: { services: CardServices; onChange(services: CardServices): void }) {
    return (
        <>
            <div className="block" style={{ position: 'relative' }}>
                <div className="block-heading">Transaction type limits</div>
                <div className="block-list">
                    <ul>
                        <ServiceEntry
                            label={'POS'}
                            value={props.services.servicePOS}
                            onChange={() =>
                                props.onChange({
                                    ...props.services,
                                    servicePOS: !props.services.servicePOS,
                                })
                            }
                        />
                        <ServiceEntry
                            label={'ATM'}
                            value={props.services.serviceATM}
                            onChange={() =>
                                props.onChange({
                                    ...props.services,
                                    serviceATM: !props.services.serviceATM,
                                })
                            }
                        />
                        <ServiceEntry
                            label={'Abroad'}
                            value={props.services.serviceAbroad}
                            onChange={() =>
                                props.onChange({
                                    ...props.services,
                                    serviceAbroad: !props.services.serviceAbroad,
                                })
                            }
                        />
                        <ServiceEntry
                            label={'Contactless'}
                            value={props.services.serviceContactless}
                            onChange={() =>
                                props.onChange({
                                    ...props.services,
                                    serviceContactless: !props.services.serviceContactless,
                                })
                            }
                        />
                        <ServiceEntry
                            label={'CNP'}
                            value={props.services.serviceCNP}
                            onChange={() =>
                                props.onChange({
                                    ...props.services,
                                    serviceCNP: !props.services.serviceCNP,
                                })
                            }
                        />
                    </ul>
                </div>
            </div>
        </>
    )
}

// function BuildCardServiceRequest(cardId: string, services: CardServices): CardServicesRequest {
// 	return {
// 		cardId: cardId,
// 		toggleServices: {
// 			posTerminal: services.servicePOS,
// 			cashWithdrawal: services.serviceATM,
// 			transactionsAbroad: services.serviceAbroad,
// 			internetTransactions: services.serviceCNP,
// 			contactlessTransactions: services.serviceContactless,
// 		},
// 	}
// }

export default function CardPage() {
    const routerParams = useParams()
    const id = routerParams['id']
    const location = useLocation()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [card, setCard] = useState<CardData | null>(null)
    const [cardLimits, setCardLimits] = useState<CardLimits | null>(null)
    const [cardServices, setCardServices] = useState<CardServices | null>(null)
    const [busy, setBusy] = useState(false)

    let backLink = '/card-management'

    if (location.state) {
        backLink = location.state as string
    }

    const fetchData = async () => {
        const r = await API.Cards.GetCardsWithBalance({ 'Filters.Id': id })

        if (r.success && r.data?.data && r.data.data.length > 0) {
            const cardData = r.data.data[0]
            setCard(cardData)
            setCardServices({
                serviceATM: !!cardData.serviceATM,
                serviceCNP: !!cardData.serviceCNP,
                serviceAbroad: !!cardData.serviceAbroad,
                servicePOS: !!cardData.servicePOS,
                serviceContactless: !!cardData.serviceContactless,
            })
        } else {
            setError(true)
            toast.error('Failed to load card data', defaultToastOptions())
        }

        const l = await API.Cards.GetCardUsageLimits(id!)

        if (l.data) {
            setCardLimits(l.data)
        } else {
            setCardLimits(null)
            // setError(true)
            // toast.error('Failed to load card limits', defaultToastOptions())
        }
    }

    //TODO: Doest this work right?
    useEffect(() => {
        if (!id) {
            return
        }

        setLoading(true)
        fetchData().then(() => setLoading(false))
    }, [])

    if (error || !routerParams['id']) {
        return <Navigate to={'/missing'} />
    }

    if (card === null) {
        return (
            <BasePage className="cd-management">
                <CircleSpinnerOverlay />
            </BasePage>
        )
    }

    const updateStatus = (status: string | null) => {
        if (status != null) {
            setCard({ ...card, status: status })
        } else {
            setLoading(true)
            fetchData().finally(() => setLoading(false))
        }
    }

    const onAliasChange = (alias: string) => {
        setCard({ ...card, processorData: { ...card.processorData!, embossName: alias } })
    }

    const headerControl = (
        <HeaderControls cardId={card.id ?? ''} cardStatus={card.status} setBusy={setBusy} updateStatus={updateStatus} />
    )

    return (
        <>
            <PageHeader title={card.cardId ?? card.alias ?? 'Card'} backLink={backLink} controls={headerControl} />
            {(busy || loading) && <CircleSpinnerOverlay />}
            <BasePage>
                <Col lg={4}>
                    <CardBlock>
                        <CardView card={card} />
                        <StatusTag status={card.status} />
                        <CardControls
                            cardId={card.id ?? ''}
                            cardStatus={card.status}
                            setBusy={setBusy}
                            updateStatus={updateStatus}
                        />
                    </CardBlock>
                    <InfoPanel card={card} onAliasChange={onAliasChange} />
                </Col>
                <Col lg={4}>
                    <CardBalanceBlock>
                        <Label>Available funds</Label>
                        <Value>{formatCurrency(card.balance ?? 0, card.currency)} </Value>
                        <div className="to-time"> November 23, 2023 | 02:20 PM</div>
                    </CardBalanceBlock>
                    {cardServices && (
                        <ServicesPanel
                            services={cardServices}
                            onChange={async (services) => {
                                setCardServices(services)
                                await API.Cards.SetCardServicesTest(id as string, services)
                            }}
                        />
                    )}
                </Col>
                {!loading && (
                    <Col lg={4}>
                        <LimitsBlock
                            id={id}
                            cardLimits={cardLimits}
                            currency={card.currency}
                            changeBusy={(b) => setBusy(b)}
                            changeCardLimit={setCardLimits}
                        />
                    </Col>
                )}
            </BasePage>
        </>
    )
}

function CardBlock(props: PropsWithChildren) {
    return <div className="block block-card">{props.children}</div>
}

function CardBalanceBlock(props: PropsWithChildren) {
    return <div className="block block-card-balance">{props.children}</div>
}